import React from "react"
import {
  Box,
  Flex,
  Select,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Alert,
  AlertIcon,
  Badge,
  Button,
  Input,
} from "@chakra-ui/react"

import { useMonthlyReport } from "./MonthlyReport.hook"
import { CasesByClient } from "./tabs/CasesByClient"
import { CasesByMonth } from "./tabs/CasesByMonth"
import { TypeOfSupport } from "./tabs/TypeOfSupport"
import { Relationship } from "./tabs/Relationship"
import { MedicalCondition } from "./tabs/MedicalCondition"
import { RtCategory } from "./tabs/RtCategory"
import { RtClients } from "./tabs/RtClients"
import { RefTable } from "./tabs/RefTable"
import { SlaCompliance } from "./tabs/SlaCompliance"

export const MonthlyReport: React.FC = () => {
  const {
    snapshots,
    selectedMonth,
    setSelectedMonth,
    isLoading,
    isGenerating,
    error,
    triggerGenerate,
    fetchSnapshots,
  } = useMonthlyReport()

  const [monthInput, setMonthInput] = React.useState("")
  const [selectedQuarter, setSelectedQuarter] = React.useState("")
  const [selectedCompanyId, setSelectedCompanyId] = React.useState("")
  const [fromMonth, setFromMonth] = React.useState("")
  const [toMonth, setToMonth] = React.useState("")
  const [refData, setRefData] = React.useState<any[]>([])

  React.useEffect(() => {
    import("../../Http/Index").then(({ getMonthlyReportRef }) => {
      getMonthlyReportRef().then((res) => setRefData(res.data?.data || []))
    })
  }, [])

  const quarters = React.useMemo(() => {
    return Array.from(new Set(snapshots.map((s) => s.quarter))).filter(Boolean)
  }, [snapshots])

  const activeTarget = React.useMemo(() => {
    if (fromMonth && toMonth) {
      return `${fromMonth}..${toMonth}`
    }
    return selectedMonth || selectedQuarter
  }, [fromMonth, toMonth, selectedMonth, selectedQuarter])

  return (
    <Box p={4}>
      {/* Header title */}
      <Text fontSize="xl" fontWeight="bold" mb={4}>Monthly Emergency Case Report</Text>

      {/* Error Feedback */}
      {error && (
        <Alert status="error" mb={4} borderRadius="md">
          <AlertIcon />
          {error}
        </Alert>
      )}

      {/* Global Controls Row: Month, Range, Quarter, Company & Generator */}
      <Flex gap={3} mb={4} align="center" wrap="wrap">
        <Select
          w="160px"
          size="sm"
          placeholder="Select Month"
          value={selectedMonth}
          onChange={(e) => {
            setSelectedMonth(e.target.value)
            setSelectedQuarter("")
            setFromMonth("")
            setToMonth("")
          }}
        >
          {snapshots.map((s) => (
            <option key={s.reportMonth} value={s.reportMonth}>
              {s.monthLabel} ({s.ticketCount} tickets)
            </option>
          ))}
        </Select>

        <Button
          size="sm"
          variant="outline"
          onClick={() => fetchSnapshots()}
          isLoading={isLoading}
        >
          Refresh
        </Button>

        {/* Custom Range: From & To */}
        <Flex align="center" gap={1} bg="gray.50" p={1} borderRadius="md" borderWidth={1}>
          <Text fontSize="xs" fontWeight="bold" color="gray.600" px={1}>Range:</Text>
          <Select
            w="130px"
            size="sm"
            placeholder="From Month"
            value={fromMonth}
            onChange={(e) => {
              setFromMonth(e.target.value)
              if (e.target.value) {
                setSelectedMonth("")
                setSelectedQuarter("")
              }
            }}
          >
            {snapshots.map((s) => (
              <option key={s.reportMonth} value={s.reportMonth}>
                {s.monthLabel}
              </option>
            ))}
          </Select>
          <Text fontSize="xs" color="gray.500">—</Text>
          <Select
            w="130px"
            size="sm"
            placeholder="To Month"
            value={toMonth}
            onChange={(e) => {
              setToMonth(e.target.value)
              if (e.target.value) {
                setSelectedMonth("")
                setSelectedQuarter("")
              }
            }}
          >
            {snapshots.map((s) => (
              <option key={s.reportMonth} value={s.reportMonth}>
                {s.monthLabel}
              </option>
            ))}
          </Select>
        </Flex>

        <Select
          w="140px"
          size="sm"
          placeholder="All Quarters"
          value={selectedQuarter}
          onChange={(e) => {
            const newQ = e.target.value
            setSelectedQuarter(newQ)
            setFromMonth("")
            setToMonth("")
            if (newQ && selectedMonth) {
              const monthSnap = snapshots.find((s) => s.reportMonth === selectedMonth)
              if (monthSnap && monthSnap.quarter !== newQ) {
                setSelectedMonth("")
              }
            }
          }}
        >
          {quarters.map((q) => (
            <option key={q} value={q}>
              {q}
            </option>
          ))}
        </Select>

        <Select
          w="180px"
          size="sm"
          placeholder="All Companies"
          value={selectedCompanyId}
          onChange={(e) => setSelectedCompanyId(e.target.value)}
        >
          {refData.map((r) => (
            <option key={r._id} value={r._id}>
              {r.companyName}
            </option>
          ))}
        </Select>

        {(selectedQuarter || selectedCompanyId || fromMonth || toMonth) && (
          <Button
            size="sm"
            variant="ghost"
            colorScheme="red"
            onClick={() => {
              setSelectedQuarter("")
              setSelectedCompanyId("")
              setFromMonth("")
              setToMonth("")
            }}
          >
            Clear Filters
          </Button>
        )}

        {activeTarget && (
          <Badge
            colorScheme={fromMonth && toMonth ? "teal" : selectedMonth ? "green" : "purple"}
            fontSize="sm"
            px={2}
            py={1}
            borderRadius="md"
          >
            {fromMonth && toMonth
              ? `Range: ${snapshots.find((s) => s.reportMonth === fromMonth)?.monthLabel || fromMonth} → ${snapshots.find((s) => s.reportMonth === toMonth)?.monthLabel || toMonth}`
              : selectedMonth
              ? snapshots.find((s) => s.reportMonth === selectedMonth)?.monthLabel
              : `Quarterly: ${selectedQuarter}`}
          </Badge>
        )}

        <Flex gap={2} align="center" ml={{ md: "auto" }}>
          <Input
            type="month"
            size="sm"
            w="170px"
            value={monthInput}
            onChange={(e) => setMonthInput(e.target.value)}
          />
          <Button
            size="sm"
            colorScheme="blue"
            isLoading={isGenerating}
            onClick={() => {
              if (monthInput) {
                triggerGenerate(monthInput)
              }
            }}
            isDisabled={!monthInput}
          >
            Generate Report
          </Button>
        </Flex>
      </Flex>

      {isLoading && (
        <Flex justify="center" py={10}>
          <Spinner size="lg" />
        </Flex>
      )}

      {!isLoading && !activeTarget && (
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          Select a month, quarter, or custom month range to view the report, or generate a new one.
        </Alert>
      )}

      {!isLoading && activeTarget && (
        <Tabs variant="enclosed" colorScheme="blue" isLazy>
          <TabList overflowX="auto" flexWrap="nowrap">
            <Tab fontSize="sm" whiteSpace="nowrap">Cases by Client</Tab>
            <Tab fontSize="sm" whiteSpace="nowrap">Cases by Month</Tab>
            <Tab fontSize="sm" whiteSpace="nowrap">Type of Support</Tab>
            <Tab fontSize="sm" whiteSpace="nowrap">Relationship</Tab>
            <Tab fontSize="sm" whiteSpace="nowrap">Medical Condition</Tab>
            <Tab fontSize="sm" whiteSpace="nowrap">RT Category</Tab>
            <Tab fontSize="sm" whiteSpace="nowrap">RT Clients</Tab>
            <Tab fontSize="sm" whiteSpace="nowrap">SLA Compliance</Tab>
            <Tab fontSize="sm" whiteSpace="nowrap">Ref Table</Tab>
          </TabList>
          <TabPanels>
            <TabPanel px={0}>
              <CasesByClient selectedMonth={activeTarget} selectedQuarter={selectedQuarter} selectedCompanyId={selectedCompanyId} />
            </TabPanel>
            <TabPanel px={0}>
              <CasesByMonth selectedMonth={activeTarget} selectedCompanyId={selectedCompanyId} />
            </TabPanel>
            <TabPanel px={0}>
              <TypeOfSupport selectedMonth={activeTarget} selectedCompanyId={selectedCompanyId} />
            </TabPanel>
            <TabPanel px={0}>
              <Relationship selectedMonth={activeTarget} selectedQuarter={selectedQuarter} selectedCompanyId={selectedCompanyId} />
            </TabPanel>
            <TabPanel px={0}>
              <MedicalCondition selectedMonth={activeTarget} selectedQuarter={selectedQuarter} selectedCompanyId={selectedCompanyId} />
            </TabPanel>
            <TabPanel px={0}>
              <RtCategory selectedMonth={activeTarget} selectedCompanyId={selectedCompanyId} />
            </TabPanel>
            <TabPanel px={0}>
              <RtClients selectedMonth={activeTarget} selectedCompanyId={selectedCompanyId} />
            </TabPanel>
            <TabPanel px={0}>
              <SlaCompliance selectedMonth={activeTarget} selectedCompanyId={selectedCompanyId} />
            </TabPanel>
            <TabPanel px={0}>
              <RefTable />
            </TabPanel>
          </TabPanels>
        </Tabs>
      )}
    </Box>
  )
}
